/**
 * Equipment catalog — platform-wide BTM/BOP/power-infra SKUs, PLUS a
 * per-company overlay (Wave B1) so each tenant can curate its OWN reusable
 * priced components. This mirrors the 3-layer supplier model exactly
 * (see `curatedSupplierStore.ts`):
 *
 *   equipment_catalog/{entryId}                         — platform baseline + curated
 *   companies/{companyId}/equipment_catalog/{entryId}   — company overlay
 *
 * The company overlay is the provenance home for #28's per-unit defaults
 * (`indicativeUnitCostUsd`, and `powerPerUnitMw` once the schema carries it —
 * see crossFileNeeds) that BoM lines inherit via `catalogEntryToBomDraft`.
 */
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  setDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import type { EquipmentCatalogEntry, SCMCategory } from '../../types/supplyChain';

export const EQUIPMENT_CATALOG_COLLECTION = 'equipment_catalog';

/** Per-company overlay subcollection — mirrors `companies/{id}/suppliers`. */
export const COMPANY_EQUIPMENT_CATALOG_SUBCOLLECTION = 'equipment_catalog';

function companyEquipmentCatalogCol(companyId: string) {
  return collection(db, `companies/${companyId}/${COMPANY_EQUIPMENT_CATALOG_SUBCOLLECTION}`);
}

function parseCatalogDoc(data: Record<string, unknown>, id: string): EquipmentCatalogEntry | null {
  if (typeof data.name !== 'string' || typeof data.spec !== 'string') return null;
  if (typeof data.scmCategory !== 'string') return null;
  // Soft-delete: company-overlay entries can be deactivated rather than hard
  // deleted (mirrors curatedSupplierStore's `active === false` drop). Platform
  // baseline/curated docs never set `active`, so this is a no-op for them.
  if (data.active === false) return null;
  return {
    id,
    name: data.name,
    spec: data.spec,
    scmCategory: data.scmCategory as SCMCategory,
    tags: Array.isArray(data.tags) ? data.tags as string[] : [],
    ...(Array.isArray(data.supplierIds) ? { supplierIds: data.supplierIds as string[] } : {}),
    ...(typeof data.indicativeUnitCostUsd === 'number' ? { indicativeUnitCostUsd: data.indicativeUnitCostUsd } : {}),
    ...(data.leadTimeMonths ? { leadTimeMonths: data.leadTimeMonths as EquipmentCatalogEntry['leadTimeMonths'] } : {}),
    ...(typeof data.capacityRating === 'string' ? { capacityRating: data.capacityRating } : {}),
    ...(typeof data.powerPerUnitMw === 'number' ? { powerPerUnitMw: data.powerPerUnitMw } : {}),
    ...(typeof data.lastVerifiedAt === 'string' ? { lastVerifiedAt: data.lastVerifiedAt } : {}),
    ...(typeof data.oemGenerationProductId === 'string' ? { oemGenerationProductId: data.oemGenerationProductId } : {}),
    ...(typeof data.oemPerformanceCaseId === 'string' ? { oemPerformanceCaseId: data.oemPerformanceCaseId } : {}),
    ...(typeof data.oemElectricalMachineId === 'string' ? { oemElectricalMachineId: data.oemElectricalMachineId } : {}),
    ...(typeof data.oemElectricalRatingCaseId === 'string' ? { oemElectricalRatingCaseId: data.oemElectricalRatingCaseId } : {}),
    ...(Array.isArray(data.reactorVendorIds) ? { reactorVendorIds: data.reactorVendorIds as string[] } : {}),
    ...(typeof data.generationScreeningId === 'string' ? { generationScreeningId: data.generationScreeningId } : {}),
    source: (data.source as EquipmentCatalogEntry['source']) ?? 'admin',
  };
}

export async function fetchEquipmentCatalog(): Promise<EquipmentCatalogEntry[]> {
  try {
    const snap = await getDocs(collection(db, EQUIPMENT_CATALOG_COLLECTION));
    const out: EquipmentCatalogEntry[] = [];
    snap.forEach((d) => {
      const parsed = parseCatalogDoc(d.data() as Record<string, unknown>, d.id);
      if (parsed) out.push(parsed);
    });
    return out;
  } catch (e) {
    console.warn('[equipmentCatalogStore] fetch failed:', e);
    return [];
  }
}

/**
 * Raw generation-screening rows for the G&I envelope ADMISSIONS layer
 * (`admitGenerationScreeningEntries`). Deliberately NOT routed through
 * `parseCatalogDoc`: the admissions logic must see soft-deleted rows
 * (`active: false` = a deliberate admin hide, distinct from an absent row)
 * and judges the untrusted fields itself. Returns [] on any failure —
 * the admissions layer then falls back to the full shipped static catalog.
 */
export async function fetchGenerationScreeningDocs(): Promise<
  Array<{ docId: string; active?: unknown; generationScreeningId?: unknown; generationCatalogVersion?: unknown }>
> {
  try {
    const snap = await getDocs(query(
      collection(db, EQUIPMENT_CATALOG_COLLECTION),
      where('tags', 'array-contains', 'generation-screening'),
    ));
    const out: Array<{ docId: string; active?: unknown; generationScreeningId?: unknown; generationCatalogVersion?: unknown }> = [];
    snap.forEach((d) => {
      const data = d.data() as Record<string, unknown>;
      out.push({
        docId: d.id,
        active: data.active,
        generationScreeningId: data.generationScreeningId,
        generationCatalogVersion: data.generationCatalogVersion,
      });
    });
    return out;
  } catch (e) {
    console.warn('[equipmentCatalogStore] fetchGenerationScreeningDocs failed:', e);
    return [];
  }
}

export async function saveEquipmentCatalogEntry(entry: EquipmentCatalogEntry): Promise<void> {
  await setDoc(doc(db, EQUIPMENT_CATALOG_COLLECTION, entry.id), {
    ...entry,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteEquipmentCatalogEntry(entryId: string): Promise<void> {
  await deleteDoc(doc(db, EQUIPMENT_CATALOG_COLLECTION, entryId));
}

/**
 * Company-overlay doc shape. Carries the same `EquipmentCatalogEntry` payload
 * plus write metadata, mirroring `CompanySupplierDoc`. `active: false` is the
 * soft-delete flag honoured by `parseCatalogDoc`.
 */
export interface CompanyEquipmentCatalogDoc extends EquipmentCatalogEntry {
  updatedAt?: unknown;
  updatedByEmail?: string;
  active?: boolean;
}

/**
 * Load a company's own curated equipment-catalog overlay. Returns [] for a
 * missing companyId or on fetch failure — so resolution degrades gracefully to
 * platform-only, matching `fetchCompanySuppliers`.
 */
export async function fetchCompanyEquipmentCatalog(
  companyId: string | null | undefined,
): Promise<EquipmentCatalogEntry[]> {
  if (!companyId) return [];
  try {
    const snap = await getDocs(companyEquipmentCatalogCol(companyId));
    const out: EquipmentCatalogEntry[] = [];
    snap.forEach((d) => {
      const parsed = parseCatalogDoc(d.data() as Record<string, unknown>, d.id);
      if (parsed) out.push(parsed);
    });
    return out;
  } catch (e) {
    console.warn('[equipmentCatalogStore] fetchCompanyEquipmentCatalog failed:', e);
    return [];
  }
}

/** Upsert a company-overlay catalog entry. Mirrors `saveCompanySupplier`. */
export async function saveCompanyEquipmentCatalogEntry(
  companyId: string,
  entry: EquipmentCatalogEntry,
  meta: { updatedByEmail?: string } = {},
): Promise<void> {
  const payload: CompanyEquipmentCatalogDoc = {
    ...entry,
    active: true,
    updatedAt: serverTimestamp(),
    ...(meta.updatedByEmail ? { updatedByEmail: meta.updatedByEmail } : {}),
  };
  await setDoc(
    doc(db, `companies/${companyId}/${COMPANY_EQUIPMENT_CATALOG_SUBCOLLECTION}`, entry.id),
    payload,
  );
}

/** Hard-delete a company-overlay catalog entry. Mirrors `deleteCompanySupplier`. */
export async function deleteCompanyEquipmentCatalogEntry(
  companyId: string,
  entryId: string,
): Promise<void> {
  await deleteDoc(
    doc(db, `companies/${companyId}/${COMPANY_EQUIPMENT_CATALOG_SUBCOLLECTION}`, entryId),
  );
}

/**
 * Baseline construction moved to the Node-safe lib module so the OEM seed CLI
 * (firebase-admin) builds the exact same rows without the client SDK.
 * Re-exported here so existing callers are unchanged.
 */
export { baselineEquipmentCatalogEntries } from '../../lib/supplyChain/equipmentCatalogBaselines';

export async function batchUpsertEquipmentCatalog(
  entries: readonly EquipmentCatalogEntry[],
): Promise<number> {
  const batch = writeBatch(db);
  for (const e of entries) {
    batch.set(doc(db, EQUIPMENT_CATALOG_COLLECTION, e.id), {
      ...e,
      updatedAt: serverTimestamp(),
    });
  }
  await batch.commit();
  return entries.length;
}
